/**
 * Sync engine — coordinates push and pull.
 *
 * Mount `useSyncEngine` once at the top of the App tree (inside
 * QueryClientProvider). The hook listens to:
 *   - userId transitions (anon → authed → migrate + push + pull)
 *   - `online` window event
 *   - tab/app foreground
 *   - explicit `requestSync()` calls from mutation hooks
 *
 * Idempotent: a single in-flight guard prevents overlapping runs.
 */

import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { drainQueue } from './push';
import { pullSessions } from './pull';
import { migrateAnonymousToUser } from './migrate';

let _running = false;
let _requested = false;

/**
 * Request a sync pass. If one is already running it just sets a flag so a
 * second pass kicks off after the current one finishes.
 */
export function requestSync(opts) {
  if (_running) {
    _requested = true;
    return Promise.resolve();
  }
  return _runSync(opts);
}

async function _runSync({ userId, isAuthed, isAnonymous, queryClient, previousUserId }) {
  if (_running) {
    _requested = true;
    return;
  }
  _running = true;
  try {
    // If we just transitioned from anon → authed, migrate first.
    if (previousUserId && previousUserId.startsWith('anon-') && isAuthed && userId) {
      try {
        await migrateAnonymousToUser({ fromUserId: previousUserId, toUserId: userId });
      } catch (err) {
        console.warn('[sync] migrate failed:', err);
      }
    }

    if (isAuthed) {
      try {
        await drainQueue({ isAuthed });
      } catch (err) {
        console.warn('[sync] push failed:', err);
      }
      try {
        await pullSessions(userId);
      } catch (err) {
        // Offline / 401 / timeout — caller falls back to local Dexie reads
        console.warn('[sync] pull failed:', err?.message || err);
      }
      if (queryClient) {
        queryClient.invalidateQueries({ queryKey: ['sessions'] });
      }
    }
  } finally {
    _running = false;
    if (_requested) {
      _requested = false;
      // Defer to next tick so callers see the previous run as finished.
      setTimeout(() => _runSync({ userId, isAuthed, isAnonymous, queryClient }), 0);
    }
  }
}

/**
 * Hook to wire the engine to React lifecycle.
 *
 * @param {Object} opts
 * @param {string|null} opts.userId
 * @param {boolean} opts.isAuthed
 * @param {boolean} opts.isAnonymous
 */
export function useSyncEngine({ userId, isAuthed, isAnonymous }) {
  const queryClient = useQueryClient();
  const previousUserIdRef = useRef(null);

  // userId transitions
  useEffect(() => {
    if (!userId) return;
    const previousUserId = previousUserIdRef.current;
    previousUserIdRef.current = userId;
    _runSync({ userId, isAuthed, isAnonymous, queryClient, previousUserId });
  }, [userId, isAuthed, isAnonymous, queryClient]);

  // Online → sync
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const handler = () => {
      _runSync({ userId, isAuthed, isAnonymous, queryClient });
    };
    window.addEventListener('online', handler);
    return () => window.removeEventListener('online', handler);
  }, [userId, isAuthed, isAnonymous, queryClient]);

  // Foreground → sync
  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    const handler = () => {
      if (document.visibilityState === 'visible') {
        _runSync({ userId, isAuthed, isAnonymous, queryClient });
      }
    };
    document.addEventListener('visibilitychange', handler);
    return () => document.removeEventListener('visibilitychange', handler);
  }, [userId, isAuthed, isAnonymous, queryClient]);
}
